import React, { useState } from 'react';
import {
	TrashIcon,
	StarIcon,
	PencilIcon,
	CheckIcon,
	XMarkIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

/**
 * ESignatureCard - Displays a saved e-signature with management actions
 */
const ESignatureCard = ({
	signature,
	onSetDefault,
	onDelete,
	onRename,
	isSelected = false,
	selectable = false,
	onSelect,
	showActions = true,
	size = 'md', // 'sm', 'md', 'lg'
}) => {
	const [isEditing, setIsEditing] = useState(false);
	const [editName, setEditName] = useState(signature?.name || '');
	const [isDeleting, setIsDeleting] = useState(false);

	const handleRename = () => {
		if (editName.trim() && editName !== signature.name) {
			onRename?.(signature.id, editName.trim());
		}
		setIsEditing(false);
	};

	const handleDelete = () => {
		if (isDeleting) {
			onDelete?.(signature.id);
			setIsDeleting(false);
		} else {
			setIsDeleting(true);
		}
	};

	const handleClick = () => {
		if (selectable && onSelect) {
			onSelect(signature.id);
		}
	};

	const sizeClasses = {
		sm: 'p-2',
		md: 'p-3',
		lg: 'p-4',
	};

	const imageSizeClasses = {
		sm: 'h-8',
		md: 'h-12',
		lg: 'h-16',
	};

	return (
		<div
			onClick={handleClick}
			className={`
				relative rounded-lg border-2 transition-all
				${selectable ? 'cursor-pointer' : ''}
				${isSelected ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'}
				${sizeClasses[size]}
			`}
		>
			{/* Default Badge */}
			{signature.isDefault && (
				<div className="absolute top-2 right-2 flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
					<StarIconSolid className="h-3 w-3" />
					Default
				</div>
			)}

			{/* Signature Image */}
			<div className="mb-2">
				<img
					src={signature.signatureData}
					alt={signature.name}
					className={`w-full object-contain bg-white rounded border border-gray-100 ${imageSizeClasses[size]}`}
				/>
			</div>

			{/* Name */}
			{isEditing ? (
				<div className="flex items-center gap-1">
					<input
						type="text"
						value={editName}
						onChange={(e) => setEditName(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === 'Enter') handleRename();
							if (e.key === 'Escape') setIsEditing(false);
						}}
						className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500"
						autoFocus
					/>
					<button
						type="button"
						onClick={handleRename}
						className="p-1 text-green-600 hover:bg-green-50 rounded"
					>
						<CheckIcon className="h-4 w-4" />
					</button>
					<button
						type="button"
						onClick={() => {
							setEditName(signature.name);
							setIsEditing(false);
						}}
						className="p-1 text-gray-500 hover:bg-gray-100 rounded"
					>
						<XMarkIcon className="h-4 w-4" />
					</button>
				</div>
			) : (
				<p className="text-sm font-medium text-gray-700 truncate">
					{signature.name}
				</p>
			)}

			{/* Meta info */}
			<p className="text-xs text-gray-400 mt-0.5">
				Created {new Date(signature.createdAt || signature.created_at).toLocaleDateString()}
			</p>

			{/* Actions */}
			{showActions && !isEditing && (
				<div className="flex items-center gap-1 mt-2 pt-2 border-t border-gray-100">
					{!signature.isDefault && onSetDefault && (
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								onSetDefault(signature.id);
							}}
							className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded transition-colors"
							title="Set as default"
						>
							<StarIcon className="h-3.5 w-3.5" />
							Set default
						</button>
					)}
					{onRename && (
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								setIsEditing(true);
							}}
							className="p-1.5 text-gray-500 hover:bg-gray-100 rounded transition-colors"
							title="Rename"
						>
							<PencilIcon className="h-3.5 w-3.5" />
						</button>
					)}
					{onDelete && (
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								handleDelete();
							}}
							className={`p-1.5 rounded transition-colors ${
								isDeleting
									? 'bg-red-100 text-red-600'
									: 'text-gray-500 hover:bg-gray-100'
							}`}
							title={isDeleting ? 'Click again to confirm' : 'Delete'}
						>
							<TrashIcon className="h-3.5 w-3.5" />
						</button>
					)}
				</div>
			)}

			{/* Delete confirmation */}
			{isDeleting && (
				<div className="absolute inset-0 bg-white/90 rounded-lg flex items-center justify-center">
					<div className="text-center p-3">
						<p className="text-sm text-gray-700 mb-2">Delete this signature?</p>
						<div className="flex gap-2 justify-center">
							<button
								type="button"
								onClick={(e) => {
									e.stopPropagation();
									setIsDeleting(false);
								}}
								className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
							>
								Cancel
							</button>
							<button
								type="button"
								onClick={(e) => {
									e.stopPropagation();
									onDelete?.(signature.id);
								}}
								className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
							>
								Delete
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

/**
 * ESignatureList - Grid display of multiple signatures
 */
export const ESignatureList = ({
	signatures = [],
	onSetDefault,
	onDelete,
	onRename,
	selectedId,
	selectable = false,
	onSelect,
	emptyMessage = 'No signatures found',
	columns = 3,
}) => {
	if (signatures.length === 0) {
		return (
			<div className="text-center py-8 text-gray-500">
				<p>{emptyMessage}</p>
			</div>
		);
	}

	const gridCols = {
		1: 'grid-cols-1',
		2: 'grid-cols-2',
		3: 'grid-cols-3',
		4: 'grid-cols-4',
	};

	return (
		<div className={`grid ${gridCols[columns] || 'grid-cols-3'} gap-4`}>
			{signatures.map((sig) => (
				<ESignatureCard
					key={sig.id}
					signature={sig}
					onSetDefault={onSetDefault}
					onDelete={onDelete}
					onRename={onRename}
					isSelected={selectedId === sig.id}
					selectable={selectable}
					onSelect={onSelect}
				/>
			))}
		</div>
	);
};

export default ESignatureCard;
